import styled from '@emotion/styled'
import {css, keyframes} from '@emotion/react'
import {theme} from '../../theme' // ← direct import, no ThemeProvider needed

const spin = keyframes`to { transform: rotate(360deg); }`

// ─── Variant map ──────────────────────────────────────────────────────────────

const variantStyles = ({variant = 'primary'}) => {
  switch (variant) {
    case 'solid':
      return css`
        background: ${theme.colors.bg.inverse};
        color: ${theme.colors.text.inverse};
        border: none;
        &:hover:not(:disabled) {
          background: ${theme.colors.action.secondaryHover};
        }
      `
    case 'solid-dark-blue':
      return css`
        background: #26325f;
        color: #ffffff;
        font-weight: 600;
        box-shadow: 0px 1px 3px 0px #0000001a;
        border: none;
        &:hover:not(:disabled) {
          background: #1a2347;
        }
      `

    case 'solid-gradiant':
      return css`
        font-weight: ${theme.fontWeights.semibold};
        color: ${theme.colors.text.inverse};
        background: radial-gradient(
          57.14% 57.14% at 50% 92.86%,
          #5378fc 0%,
          #29355e 100%
        );
        padding: 15px;
        border-radius: 100px;
        border: none;
        box-shadow: 0px 3px 5.3px 0px #0000002e;
      `
    case 'secondary':
      return css`
        background: ${theme.colors.bg.primary};
        color: ${theme.colors.text.primary};
        border: 1px solid ${theme.colors.border.dark};
        &:hover:not(:disabled) {
          background: ${theme.colors.bg.secondary};
        }
      `
    case 'secondary-blue':
      return css`
        background: #e1eaff;
        color: #0327a9;
        box-shadow: 0px 1px 2px 0px #0000000d;
        border: 1px solid #96acfd;
        // &:hover:not(:disabled) {
        //   background: ${theme.colors.action.secondaryHover};
        // }
      `
    case 'secondary-gray':
      return css`
        background: #d6def0;
        color: #000000;
        box-shadow: 0px 1px 2px 0px #0000000d;
        border-left: 1px solid #ffffff;
        border-radius: 80px;
      `
    case 'secondary-gray-reg':
      return css`
        background: #ffffff;
        color: #000000;
        box-shadow: 0px 1px 2px 0px #0000000d;
        border: 1px solid #000000;
        // border-radius: 80px;
      `
    case 'secondary-danger':
      return css`
        background: #fff9f6;
        color: #000000;
        box-shadow: 0px 1px 2px 0px #ffe0d1;
        border: 1px solid #ff5000;
      `
    case 'action-orange':
      return css`
        background: #ffdccc;
        color: #000000;
        height: auto !important;
        box-shadow: inset 0 0 0 1px #ff5000;
        border-radius: ${theme.radii.md};
        padding: 5px 10px 5px 10px !important;
        &:hover:not(:disabled) {
          background: #ff5000;
          color: #ffffff;
        }
      `
    case 'secondary-toggle-inactive':
      return css`
        background: none;
        color: ${theme.colors.text.primary};
        box-shadow: none;
        border: none;
        border-radius: 0;
      `
    case 'secondary-toggle-active':
      return css`
        background: #ffffff;
        color: #0327a9;
        // box-shadow: 0px 1px 2px 0px #0327a9;
        border: 1px solid #0327a9;
        border-radius: 5px;
        font-weight: bold;
      `
    case 'secondary-field-blue':
      return css`
        background: #dfe5f7;
        color: #0049d4;
        border-radius: 5px;
        padding: 2px;
        gap: 5px;
        font-weight: bold;
      `
    case 'secondary-field-danger':
      return css`
        background: ${theme.colors.status.danger.bg};
        color: ${theme.colors.status.danger.text};
        border-radius: 5px;
        padding: 2px;
        gap: 5px;
        font-weight: bold;
      `
    case 'danger':
      return css`
        background: ${theme.colors.status.danger.bg};
        color: ${theme.colors.status.danger.text};
        border: 1px solid ${theme.colors.status.danger.border};
        &:hover:not(:disabled) {
          background: ${theme.colors.status.danger.border};
        }
      `
    case 'primary':
    default:
      return css`
        background: ${theme.colors.action.primary};
        color: ${theme.colors.action.primaryText};
        &:hover:not(:disabled) {
          background: ${theme.colors.action.primaryHover};
        }
      `
  }
}

// ─── Size map ─────────────────────────────────────────────────────────────────

const sizeStyles = ({size = 'md'}) => {
  switch (size) {
    case 'xl':
      return css`
        height: 24px;
        padding: 0 ${theme.space[2]};
        font-size: ${theme.fontSizes.sm};
      `
    case 'sm':
      return css`
        height: 32px;
        padding: 0 ${theme.space[3]};
        font-size: ${theme.fontSizes.sm};
      `
    case 'lg':
      return css`
        height: 46px;
        padding: 0 ${theme.space[6]};
        font-size: ${theme.fontSizes.lg};
      `
    case 'md':
    default:
      return css`
        height: 36px;
        padding: 0 ${theme.space[4]};
        font-size: ${theme.fontSizes.base};
      `
  }
}

// ─── Styled components ────────────────────────────────────────────────────────

export const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.space[1.5]};
  padding: 0 ${theme.space[3]};
  font-size: ${theme.fontSizes.small};
  font-weight: ${theme.fontWeights.medium};
  border: none;
  border-radius: ${theme.radii.md};
  cursor: pointer;
  white-space: nowrap;
  width: ${({fullWidth}) => (fullWidth ? '80%' : 'auto')};
  transition: background ${theme.transitions.normal},
    opacity ${theme.transitions.normal}, transform ${theme.transitions.fast};
  box-shadow: 0px 1px 2px 0px #0000000d;

  ${variantStyles}
  ${sizeStyles}

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.border.focus};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: currentColor;
  border-radius: ${theme.radii.full};
  animation: ${spin} 0.7s linear infinite;
`
